import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import axios, { CancelToken } from "axios";
import validator from "validator";
import moment from "moment";

import { StatusCode } from "api/protocols";
import { GetEmailTemplate } from "api/rpc/2024-04/facilityAdmin/facility/emailTemplate";

import { showError } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import { ignoreOrderCompare } from "helpers/Helpers";

import FormLayout from "components/form/FormLayout";
import Sheet from "components/sheet/Sheet";
import Tabs from "components/tabs/Tabs";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import { Select } from "components/select/index";
import TextEditor from "components/textEditor/textEditor";
import TagInput from "components/tagInput/TagInput";
import Spin from "components/spin/spin";
import Portal from "elements/Portal";
import { TEmailTemplate } from "pages/secure/facility/settings/facility/emailTemplates/EmailTemplates";
import { Dictionary, groupBy } from "lodash";
import { ICourse } from "redux/reducers/models/facility";

export interface IEmailTeeSheet {
  subject: string;
  content: string;
  recipientIds: number[];
  newEmail: string;
  additionalEmails: string[];
}

export type TRecipient = {
  id: number;
  start_time: string;
  division_id: number;
  course_id: number;
};

interface IRecipientGroup {
  id: number;
  title: string;
  recipients: TRecipient[];
}

type TEmailTeesheetModalProps = {
  state: { isOpen: boolean } & IEmailTeeSheet;
  update: (newState: IEmailTeeSheet) => void;
  close: () => void;
  recipients: TRecipient[];
  onOk: () => void;
  courses: ICourse[];
};

export default function EmailTeesheetModal(props: TEmailTeesheetModalProps) {
  const { t } = useTranslation();
  const { state, update, close, recipients, onOk, courses } = props;

  const dispatch = useAppDispatch();
  const [selected, setSelected] = useState(0);

  const [emailTemplates, setEmailTemplates] = useState<TEmailTemplate[]>(undefined);

  const recipientGroups: IRecipientGroup[] = useMemo(() => {
    const updatedRecipientGroups: IRecipientGroup[] = [];
    const dictionaryGroupedRecipients: Dictionary<TRecipient[]> = groupBy(
      recipients,
      (recipient) => `${recipient.course_id}-${recipient.division_id}`,
    );

    Object.keys(dictionaryGroupedRecipients).forEach((compositeKey) => {
      const splitKey = compositeKey.split("-");
      const courseId = Number(splitKey?.[0]);
      const divisionId = Number(splitKey?.[1]);

      const foundCourse = courses?.find((course) => course.id === courseId);
      const foundDivision = foundCourse?.divisions?.find((division) => division.id === divisionId);

      updatedRecipientGroups.push({
        id: divisionId,
        title: foundCourse && foundDivision ? `${foundCourse.full_name}: ${foundDivision.title}` : "",
        recipients: dictionaryGroupedRecipients[compositeKey],
      });
    });

    return updatedRecipientGroups;
  }, [recipients, courses]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (state.isOpen) {
      void loadEmailTemplates(source.token);
    }
    return () => source.cancel();
  }, [state.isOpen]);

  // Load templates on open
  async function loadEmailTemplates(token?: CancelToken) {
    if (emailTemplates !== undefined) {
      setEmailTemplates(undefined);
    }

    const res = await GetEmailTemplate({ type: "tee_sheet" }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading email templates")));
    }

    setEmailTemplates(res.status !== StatusCode.OK ? [] : res.data);
  }

  // Only update state if template is found
  function handleTemplateChange(id: number) {
    if (emailTemplates?.some((template) => template.id === id)) {
      const template = emailTemplates.find((template) => template.id === id);
      update({ ...state, subject: template.subject, content: template.content });
    }
  }

  function handleAddNewEmail(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === ",") {
      const emails = state.additionalEmails.slice();
      const email = state.newEmail;

      if (!validator.isEmail(email)) {
        dispatch(showError(t("Please enter a valid email")));
        return;
      }
      let isAdded = false;

      emails.filter((filtered) => {
        if (filtered.toLowerCase() === email.toLowerCase()) {
          isAdded = true;
        }
      });

      if (isAdded) {
        dispatch(showError(t("Email already added")));
        return;
      }

      emails.push(email);
      update({ ...state, additionalEmails: emails, newEmail: "" });
    }
  }

  function handleRemoveNewEmail(index: number) {
    const emails = state.additionalEmails.slice();
    emails.splice(index, 1);

    update({ ...state, additionalEmails: emails });
  }

  function handleMissingInfo() {
    if (state.subject === "") {
      dispatch(showError(t("Missing Email Subject")));
      return;
    } else if (state.content === "") {
      dispatch(showError(t("Missing Email Content")));
      return;
    } else if (state.recipientIds.length === 0 && state.additionalEmails.length === 0) {
      dispatch(showError(t("Missing Email Recipients")));
      return;
    }
    onOk();
  }

  return (
    <Portal isMounted={state.isOpen}>
      <Sheet
        open={state.isOpen}
        size="medium"
        closable
        cancelText={t("Close")}
        okText={t("Send Email")}
        title={t("Email")}
        onCancel={() => {
          close(), setSelected(0);
        }}
        onOk={handleMissingInfo}
      >
        <Tabs tabs={tabs(t)} selected={selected} onSelect={(index) => setSelected(index)}>
          {/* Booking Details */}
          {selected === 0 ? (
            <div className="mt-4 flex flex-col gap-2">
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    type="text"
                    label={t("Subject")}
                    onChange={e => update({ ...state, subject: e.target.value })}
                    value={state.subject}
                  />
                  <Select
                    label={t("Template")}
                    onChange={(value: number) => handleTemplateChange(value)}
                    defaultValue={0}
                  >
                    <Select.Option key={-1} value={0} name={t("None")}>
                      {t("None")}
                    </Select.Option>
                    {emailTemplates === undefined ? (
                      <Select.Option value={-1} disabled>
                        <div style={{ height: "40px", padding: "8px 16px" }}>
                          <Spin />
                        </div>
                      </Select.Option>
                    ) : (
                      emailTemplates.map((template) => {
                        return (
                          <Select.Option key={template.id} value={template.id} name={template.title}>
                            {template.title}
                          </Select.Option>
                        );
                      })
                    )}
                  </Select>
                </FormLayout.Group>
              </FormLayout>
              <TextEditor
                label={t("Content")}
                markdownText={state.content ?? ""}
                markdownTextOnChange={(value) => update({ ...state, content: value })}
              />
            </div>
          ) : null}
          {selected === 1 ? (
            <FormLayout>
              <FormLayout.Group>
                <div>
                  <div className="mt-4 mb-4">
                    <Checkbox
                      id={"select-all"}
                      size="medium"
                      checked={ignoreOrderCompare(
                        recipients.map((val) => val.id),
                        state.recipientIds,
                      )}
                      onChange={() =>
                        update({
                          ...state,
                          recipientIds: ignoreOrderCompare(
                            recipients.map((val) => val.id),
                            state.recipientIds,
                          )
                            ? []
                            : [...recipients.map((val) => val.id)],
                        })
                      }
                      label={t("Select All")}
                    />
                  </div>

                  <div>
                    {recipientGroups.map((recipientGroup) => {
                      return (
                        <div key={recipientGroup.id}>
                          <div className="my-2">{recipientGroup.title}</div>
                          {recipientGroup.recipients.map((recipient) => {
                            const checked = state.recipientIds.some((val) => val === recipient.id);

                            return (
                              <Checkbox
                                key={recipient.id}
                                size="large"
                                checked={checked}
                                onChange={() =>
                                  update({
                                    ...state,
                                    recipientIds: checked
                                      ? state.recipientIds.filter((val) => val !== recipient.id)
                                      : [...state.recipientIds, recipient.id],
                                  })
                                }
                                label={moment(recipient.start_time, "hh:mm:ss").format("h:mm A")}
                              />
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="mt-4 mb-4">
                    <TagInput
                      label={t("Add Emails")}
                      value={state.newEmail}
                      onChange={(e) =>
                        update({ ...state, newEmail: e.target.value !== "," ? e.target.value : state.newEmail })
                      }
                      onKeyDown={handleAddNewEmail}
                      placeholder={t("Emails")}
                      tags={state.additionalEmails}
                      onTagClick={(index: number) => handleRemoveNewEmail(index)}
                      helpText={t("Add multiple tags with ' , ' or Enter.")}
                    />
                  </div>
                </div>
              </FormLayout.Group>
            </FormLayout>
          ) : null}
        </Tabs>
      </Sheet>
    </Portal>
  );
}

const tabs = (t: TFunction<"translation", undefined>) => [
  {
    id: "email-1",
    content: t("Email"),
    panelID: "email-content-1",
  },

  {
    id: "recipients-1",
    content: t("Recipients"),
    panelID: "recipients-content-1",
  },
];
